import { CheckCircle2, ShoppingCart } from 'lucide-react';

import { useReorderSuggestions } from '@/api/hooks/useReorderSuggestions';
import type { Medicine } from '@/api/types';
import { Can } from '@/auth/guards';
import { PERMISSIONS } from '@/auth/permissions';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Everything at or below its minimum, with a suggested order quantity from
 * 30-day sales velocity. "Order" hands the medicine straight to the new-PO
 * dialog rather than making the pharmacist look it up again.
 */
export function ReorderSuggestions({ onOrder }: { onOrder: (medicine: Medicine, suggestedQty: number) => void }) {
  const { data, isLoading } = useReorderSuggestions();

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-20" />
        ))}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="flex flex-col items-center gap-2 border-status-ok/30 bg-status-ok/5 py-12 text-center">
        <CheckCircle2 className="h-8 w-8 text-status-ok" />
        <p className="text-sm font-medium text-status-ok">All medicines are above minimum stock.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">
        Medicines at or below minimum stock. Suggested quantity is based on 30-day sales velocity.
      </p>
      {data.map(({ medicine, avgDailySales, suggestedOrderQty }) => (
        <Card
          key={medicine.id}
          className={`flex items-center gap-4 p-4 ${medicine.stock === 0 ? 'border-status-out/40' : 'border-status-low/40'}`}
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="truncate font-medium">{medicine.name}</p>
              <Badge variant={medicine.stock === 0 ? 'out' : 'low'}>
                {medicine.stock === 0 ? 'Out of stock' : 'Low stock'}
              </Badge>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {medicine.supplier?.name ?? 'No supplier'} · avg {avgDailySales.toFixed(1)}/day
            </p>
          </div>
          <div className="shrink-0 text-right">
            <p className="tabular text-lg font-semibold">{suggestedOrderQty}</p>
            <p className="text-xs text-muted-foreground">suggested</p>
          </div>
          <Can permission={PERMISSIONS.purchasesCreate}>
            <Button size="sm" onClick={() => onOrder(medicine, suggestedOrderQty)}>
              <ShoppingCart /> Order
            </Button>
          </Can>
        </Card>
      ))}
    </div>
  );
}